import { Injectable, Signal, signal } from '@angular/core';
import {
    AccountServiceProxy,
    IsTenantAvailableInput,
    IsTenantAvailableOutput
} from '@shared/service-proxies/service-proxies';
import { map, Observable } from 'rxjs';
import { AppTenantAvailabilityState } from '@shared/AppEnums';
import { SessionStorageService } from '@core/storage/session-storage.service';
import { StorageKey } from '@core/storage/storage-keys.enum';
import { AppConsts } from '@shared/AppConsts';

@Injectable({
    providedIn: 'root',
})
export class MultiTenancyService {
    private _tenancyUrl = signal<string>(this._initTenancyUrl());
    private _tenantId = signal<number>(abp.multiTenancy.getTenantIdCookie());

    constructor(
        private accountServiceProxy: AccountServiceProxy,
        private sessionStorage: SessionStorageService,
    ) {}

    get tenancyUrl(): Signal<string> {
        return this._tenancyUrl.asReadonly();
    }

    set tenancyUrl(value: string) {
        this._tenancyUrl.set(value);
    }

    get tenantId(): Signal<number> {
        return this._tenantId.asReadonly();
    }

    checkTenantValidity(): Observable<boolean> {
        return this.getTenantId().pipe(
            map((res) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                const tenantAvailable = !!res.tenantId && res.state === AppTenantAvailabilityState.Available;
                if (tenantAvailable) {
                    this._saveTenantInfo(res);
                    return true;
                } else {
                    return false;
                }
            }),
        );
    }

    protected getTenantId(): Observable<IsTenantAvailableOutput> {
        const tenancyUrl = this.tenancyUrl();
        return this.accountServiceProxy.isTenantAvailable(
            new IsTenantAvailableInput({
                tenancyUrl,
            }),
        );
    }

    private _initTenancyUrl(): string {
        const tenantUrlSaved = this.sessionStorage.getItem(StorageKey.tenantUrl);
        if (tenantUrlSaved && AppConsts.showTenantSelection) {
            return tenantUrlSaved as string;
        } else {
            const { hostname, protocol } = window.location;
            return `${protocol}//${hostname}`;
        }
    }

    private _saveTenantInfo(output: IsTenantAvailableOutput): void {
        abp.multiTenancy.setTenantIdCookie(output.tenantId);
        this._tenantId.set(output.tenantId);
    }
}
